import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { MapPin, Phone, Mail, Clock } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import contattiImg from '@/assets/images/sections/Contatti.jpg'

interface FormData {
  name: string
  email: string
  phone: string
  subject: string
  message: string
}

export function Contacts() {
  const { t, i18n } = useTranslation(['contacts', 'forms', 'common'])
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>()

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contacts:info.address.label'),
      content: t('contacts:info.address.value'),
    },
    {
      icon: Phone,
      title: t('contacts:info.phone.label'),
      content: t('contacts:info.phone.value'),
      href: 'tel:+390600000000',
    },
    {
      icon: Mail,
      title: t('contacts:info.email.label'),
      content: t('contacts:info.email.value'),
      href: 'mailto:info@bfwellness.it',
    },
    {
      icon: Clock,
      title: t('contacts:info.hours.label'),
      content: t('contacts:info.hours.value'),
    },
  ]

  const onSubmit = (data: FormData) => {
    console.log('Form submitted:', data)
    alert(t('contacts:success'))
  }

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('contacts:meta.title')}</title>
        <meta name="description" content={t('contacts:meta.description')} />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gray-900">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${contattiImg})` }}
        />
        <div className="relative container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('contacts:hero.title')}
            </h1>
            <p className="text-xl text-white/90">
              {t('contacts:hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                {t('contacts:info.title')}
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8">
                {t('contacts:info.description')}
              </p>

              <div className="space-y-4 sm:space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-3 sm:gap-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <item.icon className="h-4 w-4 sm:h-5 sm:w-5 text-accent-blue" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                        {item.title}
                      </h3>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-sm sm:text-base text-gray-700 hover:text-accent-blue transition-colors break-all"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-sm sm:text-base text-gray-700">{item.content}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Map */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-6 sm:mt-8 h-48 sm:h-64 rounded-lg overflow-hidden shadow-lg"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2984.642852677881!2d12.6255!3d41.4485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDI2JzU0LjYiTiAxMsKwMzcnMzEuOCJF!5e0!3m2!1sit!2sit!4v1000000000000!5m2!1sit!2sit"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="BF Wellness Location"
                />
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                {t('contacts:form.title')}
              </h2>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="bg-gray-50 rounded-lg p-4 sm:p-8"
              >
                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('forms:labels.fullName')} *
                  </label>
                  <Input
                    {...register('name', { required: t('forms:validation.nameRequired') })}
                    placeholder={t('forms:placeholders.name')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('forms:labels.email')} *
                    </label>
                    <Input
                      type="email"
                      {...register('email', {
                        required: t('forms:validation.emailRequired'),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t('forms:validation.emailInvalid')
                        }
                      })}
                      placeholder={t('forms:placeholders.email')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('forms:labels.phone')}
                    </label>
                    <Input
                      type="tel"
                      {...register('phone')}
                      placeholder={t('forms:placeholders.phone')}
                    />
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('forms:labels.subject')} *
                  </label>
                  <Input
                    {...register('subject', { required: t('forms:validation.subjectRequired') })}
                    placeholder={t('forms:placeholders.subject')}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-500">{errors.subject.message}</p>
                  )}
                </div>

                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('forms:labels.message')} *
                  </label>
                  <Textarea
                    {...register('message', { required: t('forms:validation.messageRequired') })}
                    placeholder={t('forms:placeholders.message')}
                    rows={4}
                    className="sm:rows-5"
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
                  )}
                </div>

                <Button type="submit" size="lg" className="w-full">
                  {t('common:buttons.sendMessage')}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
